import { useCallback, useEffect, useRef, useState, type MutableRefObject } from 'react';
import type { UseFormSetValue } from 'react-hook-form';
import {
  isAppointmentFormDirty,
  type AppointmentFormMode,
  type AppointmentFormValues,
} from '../utils/appointmentForm';
import { shouldClearDoctorForNotWorking } from '../utils/bookingAnchor';
import type { BookingAnchor } from '../utils/bookingAnchor';
import type { BookingDoctorOption } from '@/modules/scheduling/api/availability.api';
import type { Patient } from '@/shared/types/patient';
import type { Lead } from '@/modules/lead/api/lead.api';
import { useUnsavedChangesGuard } from './useUnsavedChangesGuard';

type TextDraft = {
  reasonForVisit: string;
  notes: string;
};

type SetFieldValue = UseFormSetValue<AppointmentFormValues>;

export type UseAppointmentFormLifecycleArgs = {
  mode: AppointmentFormMode;
  initialValues: AppointmentFormValues;
  values: AppointmentFormValues;
  submitting: boolean;
  scopeClinicId: string;
  setScopeClinicId: (clinicId: string) => void;
  setFieldValue: SetFieldValue;
  prefillPatientId?: string | null;
  prefillLeadId?: string | null;
  prefillClinicId?: string | null;
  prefillDoctorId?: string | null;
  prefillDate?: string | null;
  prefillStartTime?: string | null;
  patientSeedPending: boolean;
  leadSeedPending: boolean;
  /** True when seed get-by-id failed — stop waiting for prefill. */
  patientSeedError?: boolean;
  leadSeedError?: boolean;
  selectedPatient: Patient | null;
  selectedLead: Lead | null;
  doctors: BookingDoctorOption[];
  /** True while the roster is missing or still the previous date's placeholder. */
  doctorsPending: boolean;
  bookingAnchor: BookingAnchor;
  onClinicSynced?: () => void;
  textFieldsRef: MutableRefObject<TextDraft>;
  backTo?: string;
  onBack?: () => void;
};

/**
 * Prefill, baseline/dirty tracking, clinic sync, and doctor-clear effects for AppointmentForm.
 */
export function useAppointmentFormLifecycle({
  mode,
  initialValues,
  values,
  submitting,
  scopeClinicId,
  setScopeClinicId,
  setFieldValue,
  prefillPatientId = null,
  prefillLeadId = null,
  prefillClinicId = null,
  prefillDoctorId = null,
  prefillDate = null,
  prefillStartTime = null,
  patientSeedPending,
  leadSeedPending,
  patientSeedError = false,
  leadSeedError = false,
  selectedPatient: _selectedPatient,
  selectedLead: _selectedLead,
  doctors,
  doctorsPending,
  bookingAnchor,
  onClinicSynced,
  textFieldsRef,
  backTo,
  onBack,
}: UseAppointmentFormLifecycleArgs) {
  const prefillAppliedRef = useRef(false);
  const schedulePrefillAppliedRef = useRef(false);
  const baselineCapturedRef = useRef(mode === 'edit');
  const [baseline, setBaseline] = useState<AppointmentFormValues>(initialValues);
  const [baselineReady, setBaselineReady] = useState(mode === 'edit');
  const [textDraft, setTextDraft] = useState<TextDraft>({
    reasonForVisit: initialValues.reasonForVisit,
    notes: initialValues.notes,
  });

  const hasSchedulePrefill = Boolean(
    prefillClinicId || prefillDoctorId || prefillDate || prefillStartTime
  );
  const hasPatientPrefill = Boolean(prefillPatientId);

  useEffect(() => {
    if (mode !== 'edit') return;
    setBaseline(initialValues);
    setTextDraft({
      reasonForVisit: initialValues.reasonForVisit,
      notes: initialValues.notes,
    });
    textFieldsRef.current = {
      reasonForVisit: initialValues.reasonForVisit,
      notes: initialValues.notes,
    };
    baselineCapturedRef.current = true;
    setBaselineReady(true);
  }, [mode, initialValues, textFieldsRef]);

  useEffect(() => {
    if (mode !== 'create' || baselineCapturedRef.current) return;
    if (hasPatientPrefill && !prefillAppliedRef.current) return;
    if (hasSchedulePrefill && !schedulePrefillAppliedRef.current) return;
    if (scopeClinicId && values.clinicId !== scopeClinicId) return;

    const snapshot: AppointmentFormValues = {
      ...values,
      reasonForVisit: textFieldsRef.current.reasonForVisit,
      notes: textFieldsRef.current.notes,
    };
    baselineCapturedRef.current = true;
    setBaseline(snapshot);
    setTextDraft({
      reasonForVisit: snapshot.reasonForVisit,
      notes: snapshot.notes,
    });
    setBaselineReady(true);
  }, [
    mode,
    values,
    scopeClinicId,
    hasPatientPrefill,
    hasSchedulePrefill,
    textFieldsRef,
  ]);

  const isDirty =
    baselineReady &&
    !submitting &&
    isAppointmentFormDirty(values, baseline, textDraft);

  const {
    dialogOpen,
    requestNavigate,
    requestBack,
    requestLeave,
    allowNextNavigation,
    stay,
    leave,
  } = useUnsavedChangesGuard(isDirty);

  const handleBack = useCallback(() => {
    if (onBack) {
      requestLeave(onBack);
      return;
    }
    if (backTo) {
      requestNavigate(backTo);
      return;
    }
    requestBack();
  }, [onBack, backTo, requestLeave, requestNavigate, requestBack]);

  useEffect(() => {
    if (!scopeClinicId) return;
    if (values.clinicId === scopeClinicId) return;
    if (mode === 'edit' && values.clinicId) return;
    void setFieldValue('clinicId', scopeClinicId);
    void setFieldValue('chairId', '');
    onClinicSynced?.();
  }, [scopeClinicId, values.clinicId, mode, setFieldValue, onClinicSynced]);

  useEffect(() => {
    if (mode !== 'create' || prefillAppliedRef.current) return;

    if (prefillLeadId) {
      if (leadSeedPending) return;
      if (leadSeedError) {
        prefillAppliedRef.current = true;
        return;
      }
      // Apply as soon as seed settles — do not wait for Autocomplete value
      // (selectedLead requires leadId, which this effect sets).
      void setFieldValue('subjectType', 'lead');
      void setFieldValue('leadId', prefillLeadId);
      void setFieldValue('patientId', '');
      prefillAppliedRef.current = true;
      return;
    }

    if (!prefillPatientId) return;
    if (patientSeedPending) return;
    if (patientSeedError) {
      prefillAppliedRef.current = true;
      return;
    }
    void setFieldValue('subjectType', 'patient');
    void setFieldValue('patientId', prefillPatientId);
    prefillAppliedRef.current = true;
  }, [
    mode,
    prefillPatientId,
    prefillLeadId,
    patientSeedPending,
    leadSeedPending,
    patientSeedError,
    leadSeedError,
    setFieldValue,
  ]);

  useEffect(() => {
    if (mode !== 'create' || schedulePrefillAppliedRef.current) return;
    if (!hasSchedulePrefill) return;

    if (prefillClinicId) {
      setScopeClinicId(prefillClinicId);
    }

    const parsedDate =
      prefillDate && !Number.isNaN(new Date(`${prefillDate}T12:00:00`).getTime())
        ? new Date(`${prefillDate}T12:00:00`)
        : null;

    void setFieldValue('clinicId', prefillClinicId || '');
    void setFieldValue('doctorId', prefillDoctorId || '');
    void setFieldValue('date', parsedDate);
    void setFieldValue('startTime', prefillStartTime || '');
    schedulePrefillAppliedRef.current = true;
  }, [
    mode,
    hasSchedulePrefill,
    prefillClinicId,
    prefillDoctorId,
    prefillDate,
    prefillStartTime,
    setFieldValue,
    setScopeClinicId,
  ]);

  useEffect(() => {
    if (!values.doctorId || (Boolean(values.clinicId) && doctorsPending)) return;
    if (doctors.length === 0) return;
    if (!doctors.some((doctor) => doctor.id === values.doctorId)) {
      void setFieldValue('doctorId', '');
      return;
    }
    if (mode === 'create' && shouldClearDoctorForNotWorking(bookingAnchor)) {
      const selected = doctors.find((d) => d.id === values.doctorId);
      if (selected?.status === 'not_working') {
        void setFieldValue('doctorId', '');
      }
    }
  }, [
    values.doctorId,
    values.clinicId,
    doctors,
    doctorsPending,
    setFieldValue,
    mode,
    bookingAnchor,
  ]);

  const handleTextDraftChange = useCallback(
    (draft: TextDraft) => {
      setTextDraft((prev) => {
        const prevDirty =
          prev.reasonForVisit !== baseline.reasonForVisit ||
          prev.notes !== baseline.notes;
        const nextDirty =
          draft.reasonForVisit !== baseline.reasonForVisit ||
          draft.notes !== baseline.notes;
        if (prevDirty && nextDirty) return prev;
        return draft;
      });
    },
    [baseline.reasonForVisit, baseline.notes]
  );

  const adoptSuggestedVisitKind = useCallback((visitKind: AppointmentFormValues['visitKind']) => {
    setBaseline((current) =>
      current.visitKind === visitKind ? current : { ...current, visitKind },
    );
  }, []);

  return {
    baseline,
    baselineReady,
    adoptSuggestedVisitKind,
    textDraft,
    isDirty,
    handleTextDraftChange,
    handleBack,
    dialogOpen,
    allowNextNavigation,
    stay,
    leave,
  };
}
